import { AlertTriangle, ArrowDownToLine, Boxes, PackageX, TrendingUp, UserRound } from 'lucide-react';
import type { SubdivisionStatsDto } from '@eombor/contracts';
import { Skeleton, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { useMyStats } from '../model/subdivision.queries.js';

/**
 * «MENING OMBORIM» — YUQORIDAGI KO'RSATKICHLAR.
 *
 * Nega u bor: sahifa qoldiq JADVALI bilan ochilardi va jadval «nima bor»
 * degan savolga javob beradi. Ombor ishlatadigan odamning birinchi savoli
 * esa boshqacha — «nima TUGAB qolyapti?». Jadvaldan uni faqat har satrni
 * o'qib chiqib bilish mumkin edi, chegarani esa xodim boshida saqlab
 * yurishi kerak edi.
 *
 * To'rt ko'rsatkich, chapdan o'ngga shoshilinchlik bo'yicha: kam qolgan →
 * tugagan → oxirgi kirim → oxirgi ishlatgan. Birinchi ikkitasi ISH
 * talab qiladi, keyingi ikkitasi shunchaki holat.
 *
 * Ro'yxatlar QISQA (serverda `SUBDIVISION_STATS_PREVIEW`): panel javob
 * emas, yo'l ko'rsatkichi — to'liq ro'yxat jadvalning o'zida turadi.
 *
 * SON NOLGA teng bo'lsa karta SO'NIQ chiziladi, yashirilmaydi: yo'qolib
 * qolgan karta «bu yerda hech narsa yo'q» emas, «bu yerda hech qachon
 * hech narsa bo'lmaydi» degandek tuyuladi va odam uni qidirib yurardi.
 */

/** Miqdorni uch xonagacha yaxlitlaydi — ustunlar `numeric(14,3)`. */
const qtyText = (qty: number): string => String(Math.round(qty * 1000) / 1000);

const dateText = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
};

type Tone = 'danger' | 'warn' | 'success' | 'accent' | 'neutral';

const TONE: Record<Tone, { card: string; icon: string; value: string }> = {
  danger: {
    card: 'border-danger/25 bg-danger-soft',
    icon: 'bg-danger text-[var(--color-danger-contrast,#fff)]',
    value: 'text-danger',
  },
  warn: {
    card: 'border-warn/25 bg-warn-soft',
    icon: 'bg-warn text-[var(--color-warn-contrast,#fff)]',
    value: 'text-warn',
  },
  success: {
    card: 'border-success/25 bg-success-soft',
    icon: 'bg-success text-[var(--color-success-contrast,#fff)]',
    value: 'text-success',
  },
  accent: {
    card: 'border-accent/25 bg-accent-soft',
    icon: 'bg-accent text-[var(--color-accent-contrast)]',
    value: 'text-text',
  },
  neutral: {
    card: 'border-border bg-surface-2',
    icon: 'bg-surface text-text-muted',
    value: 'text-text-muted',
  },
};

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  /** Qiymat ostidagi bir qatorli izoh — son nimani anglatishini aytadi. */
  note?: string | null;
  tone: Tone;
}

const StatCard = ({ icon, label, value, note = null, tone }: StatCardProps) => {
  const skin = TONE[tone];
  return (
    <div
      className={cn(
        'flex min-w-0 flex-1 items-center gap-3 rounded-[var(--radius-md)] border px-3.5 py-3',
        'basis-[13rem]',
        skin.card,
      )}
    >
      <span
        aria-hidden
        className={cn(
          'flex size-9 shrink-0 items-center justify-center rounded-[var(--radius-sm)]',
          skin.icon,
        )}
      >
        {icon}
      </span>
      <div className="flex min-w-0 flex-col gap-0.5">
        <dt className="truncate text-xs text-text-muted">{label}</dt>
        <dd className={cn('truncate text-lg leading-6 font-semibold tabular-nums', skin.value)}>
          {value}
        </dd>
        {note === null ? null : (
          <dd className="truncate text-xs text-text-muted">{note}</dd>
        )}
      </div>
    </div>
  );
};

/** Ro'yxat qatoridagi ulush chizig'i — sonni o'qimasdan ko'rinadi. */
const Bar = ({ percent, tone }: { percent: number; tone: 'danger' | 'accent' }) => (
  <div aria-hidden className="h-1.5 w-full rounded-full bg-surface-2">
    <div
      className={cn(
        'h-full rounded-full transition-[width] duration-[var(--motion-slow)]',
        tone === 'danger' ? 'bg-danger' : 'bg-accent',
      )}
      style={{ width: `${String(Math.max(2, Math.min(100, percent)))}%` }}
    />
  </div>
);

const LowList = ({ stats }: { stats: SubdivisionStatsDto }) => (
  <section className="flex min-w-0 flex-1 basis-[18rem] flex-col gap-2">
    <h2 className="flex items-center gap-1.5 text-xs font-semibold tracking-wide text-text-muted uppercase">
      <AlertTriangle size={13} aria-hidden />
      {t('myStock.statsLowTitle')}
    </h2>

    {stats.lowStock.length === 0 ? (
      <p className="text-sm text-text-muted">{t('myStock.statsLowEmpty')}</p>
    ) : (
      <ul className="flex flex-col gap-2">
        {stats.lowStock.map((row) => (
          <li key={row.nomenclatureId} className="flex flex-col gap-1">
            <span className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 truncate text-sm text-text">{row.name}</span>
              <span className="shrink-0 text-xs tabular-nums font-medium text-danger">
                {t('myStock.statsLowOf', {
                  qty: qtyText(row.qty),
                  min: qtyText(row.minStock),
                  unit: row.unit,
                })}
              </span>
            </span>
            <Bar percent={(row.qty / row.minStock) * 100} tone="danger" />
          </li>
        ))}
        {stats.lowCount > stats.lowStock.length ? (
          <li className="text-xs text-text-faint">
            {t('myStock.statsMore', { count: stats.lowCount - stats.lowStock.length })}
          </li>
        ) : null}
      </ul>
    )}
    <p className="text-xs text-text-faint">{t('myStock.statsLowHint')}</p>
  </section>
);

const TopList = ({ stats }: { stats: SubdivisionStatsDto }) => {
  /* Eng kattasi 100% — ulushlar bir-biriga NISBATAN o'qiladi. Mutlaq
     songa bog'lansa, hamma chiziq bir xil qisqa bo'lib qolardi. */
  const max = stats.topUsed.reduce((best, row) => Math.max(best, row.qty), 0);

  return (
    <section className="flex min-w-0 flex-1 basis-[18rem] flex-col gap-2">
      <h2 className="flex items-center gap-1.5 text-xs font-semibold tracking-wide text-text-muted uppercase">
        <TrendingUp size={13} aria-hidden />
        {t('myStock.statsTopTitle')}
      </h2>

      {stats.topUsed.length === 0 ? (
        <p className="text-sm text-text-muted">{t('myStock.statsTopEmpty')}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {stats.topUsed.map((row) => (
            <li key={row.nomenclatureId} className="flex flex-col gap-1">
              <span className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 truncate text-sm text-text">{row.name}</span>
                <span className="shrink-0 text-xs tabular-nums font-medium text-text-muted">
                  {qtyText(row.qty)} {row.unit}
                </span>
              </span>
              <Bar percent={max === 0 ? 0 : (row.qty / max) * 100} tone="accent" />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export const StockStatsBand = () => {
  const stats = useMyStats();

  if (stats.isPending) return <Skeleton className="h-28 w-full" />;
  /* Xato PANELNI yo'q qilmaydi: qoldiq jadvali o'z so'rovi bilan keladi
     va ishlayveradi. Bu yerda bir qatorli xabar yetadi. */
  if (stats.isError || stats.data === undefined) {
    return <p className="text-sm text-text-muted">{t('myStock.statsError')}</p>;
  }

  const data = stats.data;
  const lastUse = data.lastUse;

  return (
    <div className="flex flex-col gap-4">
      <dl className="flex flex-wrap gap-3">
        <StatCard
          icon={<AlertTriangle size={18} />}
          label={t('myStock.statsLow')}
          value={String(data.lowCount)}
          tone={data.lowCount > 0 ? 'danger' : 'neutral'}
        />
        <StatCard
          icon={<PackageX size={18} />}
          label={t('myStock.statsOut')}
          value={String(data.outCount)}
          tone={data.outCount > 0 ? 'warn' : 'neutral'}
        />
        <StatCard
          icon={<ArrowDownToLine size={18} />}
          label={t('myStock.statsLastIn')}
          value={data.lastReceivedAt === null ? '—' : dateText(data.lastReceivedAt)}
          /* «Bir marta keldi» degan savolga javob shu yerda: sana
             yolg'iz turganda u necha marta takrorlanganini aytmaydi. */
          note={t('myStock.statsReceipts', { count: data.receiptCount })}
          tone={data.lastReceivedAt === null ? 'neutral' : 'success'}
        />
        <StatCard
          icon={<UserRound size={18} />}
          label={t('myStock.statsLastUse')}
          value={lastUse === null ? t('myStock.statsNoUse') : lastUse.name}
          note={
            lastUse === null
              ? null
              : t('myStock.statsLastUseBy', {
                  name: lastUse.byName ?? '—',
                  qty: qtyText(lastUse.qty),
                  unit: lastUse.unit,
                })
          }
          tone={lastUse === null ? 'neutral' : 'accent'}
        />
        <StatCard
          icon={<Boxes size={18} />}
          label={t('myStock.statsAll')}
          value={String(data.itemCount)}
          tone={data.itemCount > 0 ? 'accent' : 'neutral'}
        />
      </dl>

      {/* Ikki ro'yxat YONMA-YON: biri «nimani buyurtma qilish kerak»,
          ikkinchisi «nima tez tugaydi» degan savolga javob beradi va ular
          birga o'qilganda qaror aniq bo'ladi. */}
      <div className="flex flex-wrap gap-6 rounded-[var(--radius-md)] border border-border bg-surface px-4 py-3.5">
        <LowList stats={data} />
        <TopList stats={data} />
      </div>
    </div>
  );
};
